import { useState } from 'react';
import { Button, ConfigProvider, Tooltip, theme as antdTheme } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { chrome } from '@viq-admin/theme';
import { PANEL_COLLAPSED_WIDTH } from './panelLayout';

export interface PanelToggleProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Tooltip text for the current state, e.g. "Hide IQ Panel". */
  tooltip: string;
  /** Accessible name for the current state. Defaults to `tooltip`. */
  ariaLabel?: string;
  /**
   * `navy` for the IQ Panel's fixed brand chrome (light-on-navy in both
   * themes); `surface` for panels on the themed page background.
   */
  tone: 'navy' | 'surface';
}

/**
 * The collapse/expand control shared by every side panel.
 *
 * Renders its own anchor square rather than just a button, so every panel
 * places it identically: top-left, in the column that stays visible when the
 * panel is collapsed.
 */
export function PanelToggle({ collapsed, onToggle, tooltip, ariaLabel, tone }: PanelToggleProps) {
  const { token } = antdTheme.useToken();
  // Controlled so a click can dismiss it. Left alone, antd keeps the tooltip
  // open while the cursor rests on the button — so the label flips to the
  // opposite action ("Show IQ Panel") and lingers after the click that answered it.
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const onNavy = tone === 'navy';

  return (
    <div
      style={{
        width: PANEL_COLLAPSED_WIDTH,
        height: PANEL_COLLAPSED_WIDTH,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ConfigProvider
        theme={{
          components: {
            Button: { textHoverBg: onNavy ? chrome.hoverOnNavy : token.colorFill },
          },
        }}
      >
        <Tooltip
          title={tooltip}
          placement="right"
          open={tooltipOpen}
          onOpenChange={setTooltipOpen}
          // The bubble itself must not be hoverable. antd keeps a tooltip open
          // while the cursor is over it, so a bubble that extends to the right
          // of the button becomes a hover trap: you leave the button, land on
          // the bubble, and it stays up until you travel past its far edge.
          // Ignoring the pointer means leaving the button always closes it.
          styles={{ root: { pointerEvents: 'none' } }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => {
              setTooltipOpen(false);
              onToggle();
            }}
            aria-label={ariaLabel ?? tooltip}
            aria-expanded={!collapsed}
            style={onNavy ? { color: chrome.onNavy } : undefined}
          />
        </Tooltip>
      </ConfigProvider>
    </div>
  );
}
